<template>
  <FormProvider 
    bgColor="base-200"
    borderColor="neutral"
    borderColorFocus="primary"
    borderColorHover="primary"
    labelColorFocus="primary"
  >
    <transition name="fade">
      <form v-on:submit.prevent="register" @invalid.capture="handleInvalidField"  class="w-full" v-if="isLoading">
        <div class="flex flex-col px-6">
          <div class="mt-0 text-lg text-base-content">
            Publisher Registration
          </div>
          <!-- login details -->
          <MiniSection>
            <template #header>Login Details</template>
            <div class="grid grid-rows-1 gap-3">
              <XInput type="email" label="Email"  v-model="email" :required="true" />
              <XInput
                label="Password"
                v-model="password"
                type="password"
                :required="true"
                autocomplete="new-password"
              />
              <XInput
                label="Confirm Password"
                v-model="passwordConfirmation"
                type="password"
                :required="true"
                autocomplete="new-password"
              />
            </div>
          </MiniSection>
          <!-- userdetails -->
          <MiniSection>
            <template #header>User Details</template>
            <div class="grid grid-rows-1 gap-3">
              <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                <XInput label="First Name" v-model="firstName" :required="true" />
                <XInput label="Last Name" v-model="lastName" :required="true" />
              </div>
              <XInput label="Company" v-model="company" />
              <MultiSelect
                v-model="country"
                ref="countryMultiselect"
                inputLabel="Country"
                placeholder="Country"
                :options="countries?.countries?.filter(country => !['?', 'A1', 'A2'].includes(country.code)) || []"
                trackBy="code"
                label="name"
                :required="true"
              />
              <XInput
                label="Region"
                v-model="region"
                :disabled="isDisabled"
                :required="true"
              />
              <ConnectedFields>
                <MultiSelect
                  v-model="messengerType"
                  ref="messengerMultiselect"
                  inputLabel="Messenger"
                  placeholder="Messenger"
                  :options="types"
                  trackBy="id"
                  label="name"
                  :required="true"
                  class="max-w-40"
                />
                <XInput
                  :placeholder="
                    messengerType && messengerType.name
                      ? messengerType.handle
                      : 'Handle'
                  "
                  v-model="messengerHandle"
                  :required="true"
                  :disabled="!messengerType"
                />
              </ConnectedFields>
            </div>
          </MiniSection>
          <!-- referrals -->
          <MiniSection container-class="mt-8">
            <template #header>Where did you hear about YTZ?</template>
            <div>
              <MultiSelect
                ref="referralMultiselect"
                placeholder="Select One"
                :options="referredTypeOptions.map(ref => ref.type)"
                v-model="referredByQuestion"
                :required="true"
              />
            </div>
            <div
              v-if="!referralOptionsHidden"
            >
              <div v-if="!referralInput" class="mt-2">
                <MultiSelect
                  ref="referralResponseMultiselect"
                  placeholder="Select One"
                  :options="referralOptions"
                  v-model="referredByResponse"
                  :disabled="!referredByQuestion"
                  class="mt-1"
                  :required="true"
                />
              </div>
              <div
                v-if="referralInput || referredByResponse == 'Other'"
                class="mt-2"
              >
                <XTextArea
                  ref="referralTextInput"
                  v-model="newReferral"
                  label="How did you hear about us"
                  required
                />
              </div>
            </div>
          </MiniSection>

          <MiniSection>
            <template #header>What type of offers / verticals are you interested in?</template>
            <MultiSelect
              ref="verticalsMultiselect"
              placeholder="Select Offers / Verticals"
              :options="verticalTagsOptions"
              v-model="verticalTypeTags"
              :multiple="true"
              :required="true"
            />
          </MiniSection>

          <MiniSection>
            <template #header>What geos are you interested in?</template>
            <MultiSelect
              ref="geosMultiselect"
              placeholder="Select Geos"
              :options="geoTagsOptions"
              v-model="geoLocations"
              :multiple="true"
              :required="true"
            />
          </MiniSection>

          <MiniSection>
            <template #header>{{ question1 }} <span class="x-asterisk">*</span></template>
            <XTextArea
              v-model="response1"
              required
            />
          </MiniSection>

          <MiniSection>
            <template #header>Traffic Sources</template>
            <MultiSelect
              ref="trafficMultiselect"
              placeholder="Select Traffic Sources"
              :options="trafficTagsOptions"
              v-model="trafficType"
              :multiple="true"
              :required="true"
            />
          </MiniSection>

          <!-- submit -->
          <div class="mt-10">
            <XButton
              color="primary"
              :is-loading="isSubmitting"
              style="width: 100%"
              type="submit"
              >Register</XButton>
            <ValidationErrors
            v-if="validationErrors"
            :errors="validationErrors"
            bannerClass="mt-4"
            variant="error"
          />
            <div
              class="flex justify-between border-t border-base-300 pt-4 pb-8 px-2 mt-3"
            >
              <p class="text-base-content/70">Already have an account?</p>
              <router-link :to="{ name: 'Login' }">
                <p class="link link-accent">Sign In</p>
              </router-link>
            </div>
          </div>
        </div>

      </form>
    </transition>
    <div class="w-full" v-if="!isLoading">
      <div class="flex flex-col gap-3 rounded-2xl p-5 pt-8">
        <div class="h-6 w-4/5 rounded-lg is-loading"></div>
        <div class="skeleton-label is-loading"></div>
        <div class="skeleton-input is-loading"></div>
        <div class="skeleton-input is-loading"></div>
        <div class="skeleton-input is-loading"></div>
        <div class="skeleton-label is-loading mt-6"></div>
        <div class="skeleton-input is-loading"></div>
        <div class="skeleton-input is-loading"></div>
        <div class="skeleton-input is-loading"></div>
        <div class="skeleton-input is-loading"></div>
        <div class="skeleton-input is-loading"></div>
        <div class="skeleton-input is-loading"></div>
        <div class="skeleton-label is-loading mt-6"></div>
        <div class="skeleton-input is-loading"></div>
        <div class="h-20 w-full rounded-lg is-loading mt-6"></div>
      </div>
    </div>
  </FormProvider>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useMutation } from '@vue/apollo-composable'
import { useRoute, useRouter } from 'vue-router'
import FormProvider from '@/components/common/FormProvider.vue'
import ValidationErrors from '@/components/common/ValidationErrors.vue'
import ValidationErrorsClass from '@/assets/js/ValidationErrors.js'
import REGISTER_MUTATION from '@/graphql/Auth/RegisterMutation.gql'


const route = useRoute()
const router = useRouter()

//props
const props = defineProps({
  tags: {
    type: Object,
  },
  countries: {
    type: Object,
  },
})

// Referral types matching ytrack-frontend
const referredTypeOptions = ref([
  {
    type: 'Conference',
    options: ['Affiliate World', 'Pubcon', 'SES', 'Other'],
    selection: 'dropdown'
  },
  {
    type: 'YTZ Employee',
    options: ['Kate', 'Devin', 'Megan', 'Marissa', 'Nicole'],
    selection: 'dropdown'
  },
  {
    type: 'YouTube',
    options: [],
    selection: null
  },
  {
    type: 'Facebook',
    options: [],
    selection: null
  },
  {
    type: 'LinkedIn',
    options: [],
    selection: null
  },
  {
    type: 'Newsletter',
    options: [],
    selection: null
  },
  {
    type: 'Online Search',
    options: [],
    selection: null
  },
  {
    type: 'Referred by YTZ User',
    options: [],
    selection: null
  },
  {
    type: 'Affiliate Forum',
    options: [],
    selection: 'userInput'
  },
  {
    type: 'Other',
    options: [],
    selection: 'userInput'
  }
])

// Reactive data
const isSubmitting = ref(false)
const email = ref('')
const password = ref('')
const passwordConfirmation = ref('')
const firstName = ref('')
const lastName = ref('')
const company = ref('')
const country = ref('')
const region = ref('')
const messengerType = ref('')
const messengerHandle = ref('')
const referredByQuestion = ref('')
const referredByResponse = ref('')
const question1 = ref('Which other networks do you work with?')
const response1 = ref('')
const trafficType = ref(null)
const verticalTypeTags = ref(null)
const types = ref([
  { id: 1, name: 'Telegram', handle: 'Telegram Handle' }
])
const referrerId = ref(null)
const newReferral = ref('')
const geoLocations = ref(null)
const validationErrors = ref(null)

// MultiSelect component refs
const countryMultiselect = ref(null)
const messengerMultiselect = ref(null)
const referralMultiselect = ref(null)
const referralResponseMultiselect = ref(null)
const verticalsMultiselect = ref(null)
const geosMultiselect = ref(null)
const trafficMultiselect = ref(null)

// Regular input refs for referral text input
const referralTextInput = ref(null)

onMounted(() => {
  if (route.query.ref) {
    localStorage.setItem('referrerId', route.query.ref)
  }
  referrerId.value = localStorage.getItem('referrerId')
})

onUnmounted(() => {
  // Clean up any references when component unmounts
  firstInvalidField = null
  isSubmitting.value = false
})

const { mutate: registerMutation } = useMutation(REGISTER_MUTATION)

// Validation error handling methods
const initValidationErrors = () => {
  return new ValidationErrorsClass()
}

const setValidationErrors = (error) => {
  let errors = initValidationErrors()
  errors.extractErrorsFrom(error)
  validationErrors.value = errors.errors
}

const clearValidationErrors = () => {
  validationErrors.value = null
}

let firstInvalidField = null

const handleInvalidField = (e) => {
  // Store the first invalid field (for regular inputs)
  if (!firstInvalidField) {
    firstInvalidField = e.target
    
    // Scroll to the first invalid field after a short delay
    setTimeout(() => {
      if (firstInvalidField) {
        firstInvalidField.scrollIntoView({ behavior: 'smooth', block: 'center' })
        setTimeout(() => {
          if (firstInvalidField && typeof firstInvalidField.focus === 'function') {
            firstInvalidField.focus()
          }
        }, 500)
      }
      // Reset for next submission attempt
      firstInvalidField = null
    }, 100)
  }
}

const register = (e) => {
  e.preventDefault()
  
  // Check form validity using native validation for regular inputs
  const form = e.target
  if (!form.checkValidity()) {
    // Native inputs will show their validation messages
    form.reportValidity()
    return
  }

  // Check MultiSelect validation and find first invalid field
  console.log('Checking multiselect validation...')
  console.log('country.value:', country.value)
  console.log('messengerType.value:', messengerType.value)
  console.log('referredByQuestion.value:', referredByQuestion.value)
  
  const fieldsToCheck = [
    { value: country.value?.code, name: 'Country', ref: countryMultiselect },
    { value: messengerType.value?.name, name: 'Messenger', ref: messengerMultiselect },
    { value: referredByQuestion.value, name: 'Referral source', ref: referralMultiselect },
    { value: verticalTypeTags.value?.length > 0 ? verticalTypeTags.value : null, name: 'Verticals', ref: verticalsMultiselect },
    { value: geoLocations.value?.length > 0 ? geoLocations.value : null, name: 'Geos', ref: geosMultiselect },
    { value: trafficType.value?.length > 0 ? trafficType.value : null, name: 'Traffic sources', ref: trafficMultiselect }
  ]
  
  // Check if the referral follow-up field is required and validate it
  const needsReferralResponse = !referralOptionsHidden.value && !referralInput.value
  if (needsReferralResponse && !referredByResponse.value) {
    fieldsToCheck.push({ value: null, name: 'Referral selection', ref: referralResponseMultiselect })
  }
  
  // Check if the referral text input is required and validate it  
  const needsReferralText = !referralOptionsHidden.value && (referralInput.value || referredByResponse.value === 'Other')
  if (needsReferralText && !newReferral.value) {
    fieldsToCheck.push({ value: null, name: 'Referral text', ref: referralTextInput })
  }
  
  console.log('Fields to check:', fieldsToCheck)
  
  const invalidFields = fieldsToCheck.filter(field => !field.value)
  
  console.log('Invalid fields:', invalidFields)
  
  if (invalidFields.length > 0) {
    // Focus and open the first invalid MultiSelect field using its ref
    const firstInvalidField = invalidFields[0]
    console.log('First invalid field:', firstInvalidField)
    console.log('Ref exists:', !!firstInvalidField.ref?.value)
    console.log('focusAndOpen method exists:', !!firstInvalidField.ref?.value?.focusAndOpen)
    
    if (firstInvalidField.ref?.value?.focusAndOpen) {
      // This is a MultiSelect component
      setTimeout(() => {
        console.log('Calling focusAndOpen on:', firstInvalidField.name)
        firstInvalidField.ref.value.focusAndOpen()
      }, 100)
    } else if (firstInvalidField.ref?.value) {
      // This is a regular input component, access the inner input element
      setTimeout(() => {
        console.log('Focusing regular input:', firstInvalidField.name)
        const inputElement = firstInvalidField.ref.value.input || firstInvalidField.ref.value.$el
        if (inputElement && inputElement.focus) {
          inputElement.focus()
          console.log('Successfully focused:', firstInvalidField.name)
        } else {
          console.log('Could not find focusable element for:', firstInvalidField.name)
        }
      }, 100)
    } else {
      console.log('Cannot focus field:', firstInvalidField.name, 'ref or method missing')
    }
    
    return
  }
  
  isSubmitting.value = true
  clearValidationErrors()
  
  registerMutation({
    input: {
      email: email.value,
      password: password.value,
      password_confirmation: passwordConfirmation.value,
      firstName: firstName.value,
      lastName: lastName.value,
      company: company.value,
      country: country.value.code,
      region: region.value,
      messengerType: messengerType.value.name,
      messengerHandle: messengerHandle.value,
      questions: {
        question1: {
          question: question1.value,
          answer: response1.value
        }
      },
      trafficType: trafficType.value,
      verticalTypeTags: verticalTypeTags.value,
      referrerId: referrerId.value,
      referredOption: referralTagComputed(),
      geoLocation: geoLocations.value,
      meta: {
        browserLanguage: navigator.language,
      },
    }
  })
  .then(response => {
    // Clear errors first
    clearValidationErrors()
    
    // Small delay to let browser extensions finish their work
    setTimeout(() => {
      isSubmitting.value = false
      
      // Navigate to completion page regardless of status
      router.push({ name: 'RegisterComplete' })
    }, 100)
  })
  .catch(error => {
    isSubmitting.value = false
    console.error(error)
    setValidationErrors(error)
  })
}

const referralTagComputed = () => {
  let selection = referredByQuestion.value
  switch (selection) {
    case 'Conference':
      return referredByResponse.value
    case 'YTZ Employee':
      if (referredByQuestion.value && referredByResponse.value) {
        return referredByQuestion.value + ' - ' + referredByResponse.value
      } else {
        return null
      }
    case 'YouTube':
    case 'Facebook':
    case 'LinkedIn':
    case 'Newsletter':
    case 'Online Search':
      return referredByQuestion.value
    case 'Referred by YTZ User':
      return 'YTZ User'
    case 'Affiliate Forum':
    case 'Other':
      return referredByQuestion.value + ' - ' + newReferral.value
    default:
      return null
  }
}


// Computed properties
const isLoading = computed(() => {
  return props.tags && referredTypeOptions.value
})

const isDisabled = computed(() => {
  return country.value ? false : true
})

const trafficTagsOptions = computed(() => {
  return props.tags?.tags?.data
    ?.filter(tag => tag.type === 'trafficType')
    .map(tag => tag.name) || []
})

const verticalTagsOptions = computed(() => {
  return props.tags?.tags?.data
    ?.filter(tag => tag.type === 'vertical')
    .map(tag => tag.name) || []
})

const referralOptions = computed(() => {
  let result = referredTypeOptions.value
    .filter(referredBy => referredBy.type == referredByQuestion.value)
    .map(referredBy => referredBy.options)
  return result[0] || []
})

const geoTagsOptions = computed(() => {
  return props.tags?.tags?.data
    ?.filter(tag => tag.type === 'geoLocation')
    .map(tag => tag.name) || []
})

const referralInput = computed(() => {
  let type = referredByQuestion.value
  switch (type) {
    case 'Other':
    case 'Affiliate Forum':
      return true
    default:
      return false
  }
})

const referralOptionsHidden = computed(() => {
  let selection = referredByQuestion.value
  switch (selection) {
    case 'Conference':
    case 'YTZ Employee':
    case 'Affiliate Forum':
    case 'Other':
      return false
    default:
      return true
  }
})
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 1.8s ease;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
.skeleton-input {
  @apply h-12 w-full rounded-lg;
}
.skeleton-label {
  @apply h-6 w-3/5 rounded-lg;
}
.is-loading {
  background: linear-gradient(
    to right,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.5) 50%,
    rgba(255, 255, 255, 0) 100%
  );
  background-size: 75% 100%;
  animation-duration: 1500ms;
  animation-name: headerShine;
  animation-iteration-count: infinite;
  background-repeat: no-repeat;
  animation-timing-function: ease;
  background-position: 0 0;
  background-color: #e4e4e7;
  background-blend-mode: overlay;
}
@keyframes headerShine {
  0% {
    background-position: -300% 0;
  }
  100% {
    background-position: 500% 0;
  }
}

.x-asterisk {
  @apply text-lg text-error text-lg inline-block pl-0.5;
  transform: translate(0, 0.2rem);
}
</style>